// src/services/orcamentoRender.js
// Dibuja el orçamento de un vuelo por millas con la marca Click Travel Agency,
// 100 % en canvas (sin IA): sale siempre igual. Mismo dibujo para imagen y PDF.
// Solo depende del DOM, no de React.

import { tx } from './idiomaArte';

export const C = {
  navyDeep: '#06284A', navy: '#0B4C8C', orange: '#FF6B00', orangeSoft: '#FF9A4D',
  ink: '#0B2540', muted: '#566A82', paper: '#EAF0F7', line: '#DCE5F0', white: '#FFFFFF',
  soft: '#F2F6FB', green: '#25D366',
};
export const F = { body: "'Outfit', 'Segoe UI', sans-serif", display: "'Bebas Neue', Impact, sans-serif", script: "'Caveat', 'Segoe Script', cursive" };

export const WHATSAPP_DEFECTO = '(41) 92000-4775';

// Ícono "flight" de Material (viewBox 24, nariz hacia arriba)
const AVION = 'M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z';

export const fmtFechaLarga = (iso, idioma = 'pt') => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${Number(m[3])} ${tx(idioma).meses[Number(m[2]) - 1]} ${m[1]}` : '';
};
export const fmtFechaCorta = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};
export const fmtReales = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Helpers de dibujo ────────────────────────────────────────────────
export function rr(ctx, x, y, w, h, r) {
  const k = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + k, y);
  ctx.arcTo(x + w, y, x + w, y + h, k);
  ctx.arcTo(x + w, y + h, x, y + h, k);
  ctx.arcTo(x, y + h, x, y, k);
  ctx.arcTo(x, y, x + w, y, k);
  ctx.closePath();
}

export function font(ctx, weight, size, family) { ctx.font = `${weight} ${size}px ${family}`; }

// Texto con espaciado entre letras (a mano, para no depender de ctx.letterSpacing)
export function spaced(ctx, str, x, y, spacing, align = 'left') {
  const chars = [...str];
  const widths = chars.map((ch) => ctx.measureText(ch).width);
  const total = widths.reduce((a, b) => a + b, 0) + spacing * (chars.length - 1);
  let cx = align === 'right' ? x - total : align === 'center' ? x - total / 2 : x;
  const prev = ctx.textAlign;
  ctx.textAlign = 'left';
  chars.forEach((ch, i) => { ctx.fillText(ch, cx, y); cx += widths[i] + spacing; });
  ctx.textAlign = prev;
  return total;
}

export function wrap(ctx, text, maxW) {
  const lines = [];
  String(text).split('\n').forEach((par) => {
    let line = '';
    par.split(/\s+/).filter(Boolean).forEach((word) => {
      const probe = line ? `${line} ${word}` : word;
      if (ctx.measureText(probe).width <= maxW || !line) line = probe;
      else { lines.push(line); line = word; }
    });
    if (line) lines.push(line);
  });
  return lines;
}

// Ajusta el tamaño de fuente para que el texto quepa en maxW
export function fitSize(ctx, text, weight, family, size, minSize, maxW) {
  let s = size;
  font(ctx, weight, s, family);
  while (ctx.measureText(text).width > maxW && s > minSize) { s -= 4; font(ctx, weight, s, family); }
  return s;
}

// Una sola línea: reduce la letra hasta `min` y, si aún no cabe, corta con "…".
export function lineaAjustada(ctx, texto, peso, tam, min, maxW) {
  const t = String(texto || '');
  const s = fitSize(ctx, t, peso, F.body, tam, min, maxW);
  if (ctx.measureText(t).width <= maxW) return { t, s };
  let r = t;
  while (r.length > 1 && ctx.measureText(`${r}…`).width > maxW) r = r.slice(0, -1);
  return { t: `${r.trimEnd()}…`, s };
}

export function plane(ctx, cx, cy, size, rot, color) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rot);
  ctx.scale(size / 24, size / 24);
  ctx.translate(-12, -12);
  ctx.fillStyle = color;
  ctx.fill(new Path2D(AVION));
  ctx.restore();
}

export function shadow(ctx, blur, y, alpha) {
  ctx.shadowColor = `rgba(6,40,74,${alpha})`;
  ctx.shadowBlur = blur;
  ctx.shadowOffsetY = y;
}
export const noShadow = (ctx) => { ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0; };

// Íconos de línea minimalistas (24px de caja)
export function icono(ctx, tipo, x, y, size, color) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / 24, size / 24);
  ctx.strokeStyle = color; ctx.fillStyle = color;
  ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  if (tipo === 'user') {
    ctx.beginPath(); ctx.arc(12, 8, 4, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(4, 21); ctx.quadraticCurveTo(4, 14, 12, 14); ctx.quadraticCurveTo(20, 14, 20, 21); ctx.stroke();
  } else if (tipo === 'doc') {
    rr(ctx, 5, 3, 14, 18, 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(8.5, 9); ctx.lineTo(15.5, 9); ctx.moveTo(8.5, 13); ctx.lineTo(15.5, 13); ctx.moveTo(8.5, 17); ctx.lineTo(12.5, 17); ctx.stroke();
  } else if (tipo === 'calendar') {
    rr(ctx, 3.5, 5, 17, 15, 2.5); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(3.5, 10); ctx.lineTo(20.5, 10); ctx.moveTo(8, 3); ctx.lineTo(8, 7); ctx.moveTo(16, 3); ctx.lineTo(16, 7); ctx.stroke();
  } else if (tipo === 'bag') {
    rr(ctx, 4, 8, 16, 12, 2.5); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(9, 8); ctx.lineTo(9, 5.5); ctx.quadraticCurveTo(9, 4, 10.5, 4); ctx.lineTo(13.5, 4); ctx.quadraticCurveTo(15, 4, 15, 5.5); ctx.lineTo(15, 8); ctx.stroke();
  } else if (tipo === 'seat') {
    ctx.beginPath(); ctx.moveTo(8, 3); ctx.lineTo(8, 13); ctx.lineTo(18, 13); ctx.lineTo(18, 21); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(8, 13); ctx.lineTo(8, 17); ctx.stroke();
  } else if (tipo === 'clock') {
    ctx.beginPath(); ctx.arc(12, 12, 9, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(12, 7); ctx.lineTo(12, 12); ctx.lineTo(15.5, 14); ctx.stroke();
  } else if (tipo === 'shield') {
    ctx.beginPath(); ctx.moveTo(12, 3); ctx.lineTo(20, 6); ctx.lineTo(20, 12); ctx.quadraticCurveTo(20, 18, 12, 21.5); ctx.quadraticCurveTo(4, 18, 4, 12); ctx.lineTo(4, 6); ctx.closePath(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(8.5, 12); ctx.lineTo(11, 14.5); ctx.lineTo(15.5, 9.5); ctx.stroke();
  } else if (tipo === 'check') {
    ctx.lineWidth = 2.6;
    ctx.beginPath(); ctx.moveTo(5, 12.5); ctx.lineTo(10, 17.5); ctx.lineTo(19, 7); ctx.stroke();
  } else if (tipo === 'pix') {
    ctx.save(); ctx.translate(12, 12); ctx.rotate(Math.PI / 4);
    rr(ctx, -7, -7, 14, 14, 3); ctx.stroke(); ctx.restore();
    ctx.beginPath(); ctx.arc(12, 12, 2, 0, Math.PI * 2); ctx.fill();
  } else if (tipo === 'pin') {
    ctx.stroke(new Path2D('M12 21C12 21 5 14.5 5 9.5a7 7 0 0 1 14 0C19 14.5 12 21 12 21z'));
    ctx.beginPath(); ctx.arc(12, 9.5, 2.5, 0, Math.PI * 2); ctx.stroke();
  } else if (tipo === 'bed') {
    ctx.beginPath(); ctx.moveTo(3, 6); ctx.lineTo(3, 18); ctx.moveTo(3, 14); ctx.lineTo(21, 14); ctx.moveTo(21, 18); ctx.lineTo(21, 13); ctx.quadraticCurveTo(21, 10, 18, 10); ctx.lineTo(10, 10); ctx.lineTo(10, 14); ctx.stroke();
    ctx.beginPath(); ctx.arc(6.5, 10.5, 2, 0, Math.PI * 2); ctx.stroke();
  } else if (tipo === 'bus') {
    rr(ctx, 5, 3, 14, 15, 3); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(5, 11); ctx.lineTo(19, 11); ctx.moveTo(8, 18); ctx.lineTo(8, 21); ctx.moveTo(16, 18); ctx.lineTo(16, 21); ctx.stroke();
    ctx.beginPath(); ctx.arc(8.5, 14.5, 0.6, 0, Math.PI * 2); ctx.arc(15.5, 14.5, 0.6, 0, Math.PI * 2); ctx.stroke();
  } else if (tipo === 'card') {
    rr(ctx, 3, 5.5, 18, 13, 2.5); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(3, 10); ctx.lineTo(21, 10); ctx.moveTo(6.5, 15); ctx.lineTo(10.5, 15); ctx.stroke();
  } else if (tipo === 'phone') {
    ctx.beginPath(); ctx.moveTo(6, 3.5); ctx.lineTo(10, 3.5); ctx.lineTo(11.5, 8); ctx.lineTo(9.5, 9.5); ctx.quadraticCurveTo(11, 13, 14.5, 14.5); ctx.lineTo(16, 12.5); ctx.lineTo(20.5, 14); ctx.lineTo(20.5, 18); ctx.quadraticCurveTo(12, 21.5, 3.5, 6.5); ctx.closePath(); ctx.stroke();
  }
  ctx.restore();
}

export function cargarImagen(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo cargar la imagen.'));
    img.src = src;
  });
}

export async function asegurarFuentes() {
  if (!document.fonts?.load) return;
  try {
    await Promise.all([
      document.fonts.load("400 24px 'Outfit'"), document.fonts.load("600 24px 'Outfit'"),
      document.fonts.load("700 24px 'Outfit'"), document.fonts.load("800 24px 'Outfit'"),
      document.fonts.load("40px 'Bebas Neue'"), document.fonts.load("600 40px 'Caveat'"),
    ]);
  } catch { /* si falla, se dibuja con la fuente de respaldo */ }
}

// ── Formatos de salida ───────────────────────────────────────────────
// a4: hoja para PDF/imprimir · story: 1080×1920 (WhatsApp/Instagram) · cuadrado: 1080×1080 (feed)
export const FORMATOS = {
  a4: { id: 'a4', label: 'A4', detalle: 'Hoja · PDF', w: 1131, h: 1600, escala: 1.6 },
  story: { id: 'story', label: 'Story', detalle: '1080 × 1920', w: 1080, h: 1920, escala: 1 },
  cuadrado: { id: 'cuadrado', label: 'Cuadrado', detalle: '1080 × 1080', w: 1080, h: 1080, escala: 1 },
};

// ── Cabecera: degradado azul + globo del logo + estela naranja ───────
// Se dibuja en coordenadas (0..w, 0..h); la estela y el avión van pegados a la derecha.
export function dibujarCabecera(ctx, w, h, { estela = true } = {}) {
  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, C.navyDeep);
  g.addColorStop(1, C.navy);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.beginPath(); ctx.rect(0, 0, w, h); ctx.clip();
  ctx.strokeStyle = 'rgba(255,255,255,0.07)';
  ctx.lineWidth = 3;
  const gx = w - 90; const gy = Math.min(360, h * 0.5); const R = Math.min(430, h * 0.6);
  ctx.beginPath(); ctx.arc(gx, gy, R, 0, Math.PI * 2); ctx.stroke();
  [0.28, 0.58, 0.88].forEach((k) => { ctx.beginPath(); ctx.ellipse(gx, gy, R * k, R, 0, 0, Math.PI * 2); ctx.stroke(); });
  [-0.62, -0.3, 0, 0.3, 0.62].forEach((k) => {
    const yy = gy + R * k; const half = Math.sqrt(R * R - (R * k) ** 2);
    ctx.beginPath(); ctx.moveTo(gx - half, yy); ctx.lineTo(gx + half, yy); ctx.stroke();
  });
  ctx.restore();

  if (estela) {
    const ox = w - 1131;
    ctx.save();
    ctx.beginPath(); ctx.rect(0, 0, w, h); ctx.clip();
    const trail = ctx.createLinearGradient(540 + ox, 400, w, 230);
    trail.addColorStop(0, 'rgba(255,107,0,0)');
    trail.addColorStop(0.55, 'rgba(255,107,0,0.95)');
    trail.addColorStop(1, C.orangeSoft);
    ctx.fillStyle = trail;
    ctx.beginPath();
    ctx.moveTo(560 + ox, 396);
    ctx.quadraticCurveTo(820 + ox, 396, 1050 + ox, 262);
    ctx.quadraticCurveTo(830 + ox, 352, 560 + ox, 396);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    plane(ctx, 1066 + ox, 250, 64, Math.PI / 2 - 0.5, C.orange);
  }
}

export function recuadroLogo(ctx, logo, x, y, w, h, radio) {
  ctx.save(); shadow(ctx, 40, 14, 0.35);
  ctx.fillStyle = C.white; rr(ctx, x, y, w, h, radio); ctx.fill();
  ctx.restore();
  if (!logo) return;
  const sw = 800; const sh = 440; const sx = 90; const sy = 232; // recorta el margen en blanco del PNG
  const dw = w * 0.855; const dh = dw * (sh / sw);
  ctx.save();
  rr(ctx, x, y, w, h, radio); ctx.clip();
  ctx.globalCompositeOperation = 'multiply';
  ctx.drawImage(logo, sx, sy, sw, sh, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  ctx.restore();
}

// Cabecera de los formatos largos (A4/Story). Diseñada en 800 de alto y escalada con hk
// para que quepa en el alto que sobre; si sobra espacio se reparte hacia abajo.
// textos opcionales: { etiqueta, titulo, pie }. Por defecto: "SEU PRÓXIMO DESTINO" + ciudad + país.
export function cabeceraLarga(ctx, d, logo, W, heroH, textos = null) {
  const T = tx(d.idioma);
  const t = textos || {
    etiqueta: T.proximoDestino,
    titulo: String(d.destinoCiudad || d.destinoCodigo || 'DESTINO').toUpperCase(),
    pie: String(d.destinoPais || 'BRASIL').toUpperCase(),
  };
  const hk = Math.max(0.54, Math.min(1, heroH / 800));
  const w = W / hk; const h = heroH / hk;
  const extra = Math.max(0, h - 800);
  ctx.save();
  ctx.scale(hk, hk);
  dibujarCabecera(ctx, w, h);
  recuadroLogo(ctx, logo, 56, 56, 480, 270, 36);

  ctx.textAlign = 'right';
  font(ctx, 600, 56, F.script); ctx.fillStyle = C.white;
  ctx.fillText(T.lema1, w - 56, 132);
  ctx.fillStyle = C.orangeSoft;
  ctx.fillText(T.lema2, w - 56, 192);
  ctx.textAlign = 'left';

  const yo = extra * 0.55;
  font(ctx, 600, 24, F.body); ctx.fillStyle = C.orangeSoft;
  spaced(ctx, t.etiqueta, 56, 432 + yo, 7);
  const tam = fitSize(ctx, t.titulo, 400, F.display, 214, 84, w - 112);
  ctx.fillStyle = C.white;
  ctx.fillText(t.titulo, 52, 432 + yo + tam * 0.86);
  if (t.pie) {
    const yPais = 432 + yo + tam * 0.86 + 58;
    font(ctx, 600, 28, F.body); ctx.fillStyle = 'rgba(255,255,255,0.82)';
    const wPais = spaced(ctx, t.pie, 58, yPais, 9);
    ctx.fillStyle = C.orange; rr(ctx, 58, yPais + 16, Math.max(120, wPais * 0.6), 7, 3.5); ctx.fill();
  }
  ctx.restore();
}

// ── Componentes (se dibujan en (0,0) con ancho `w`; el llamador los escala) ──
const H_META = 92; const H_VUELO = 312; const H_PRECIO = 250; const H_PAGO = 140; const GAP = 26;

function cMeta(ctx, d, w) {
  const T = tx(d.idioma);
  const hoyIso = new Date().toISOString().slice(0, 10);
  const cols = esMulti(d) ? [] : [{ icon: 'user', label: T.soloIda, value: `${d.adultos || 1} ${(d.adultos || 1) > 1 ? 'ADULTOS' : 'ADULTO'}` }];
  cols.push(
    { icon: 'calendar', label: T.fechaOrc, value: fmtFechaCorta(d.fechaOrcamento || hoyIso) },
  );
  if (d.cliente) cols.push({ icon: 'user', label: T.preparadoPara, value: d.cliente });
  const cw = w / cols.length;
  cols.forEach((col, i) => {
    const cx = i * cw;
    ctx.fillStyle = C.soft; ctx.beginPath(); ctx.arc(cx + 28, 36, 28, 0, Math.PI * 2); ctx.fill();
    icono(ctx, col.icon, cx + 28 - 14, 36 - 14, 28, C.navy);
    font(ctx, 600, 16, F.body); ctx.fillStyle = C.muted;
    spaced(ctx, col.label, cx + 70, 26, 1.6);
    const vs = fitSize(ctx, col.value, 800, F.body, 32, 20, cw - 86);
    ctx.fillStyle = C.ink; ctx.fillText(col.value, cx + 70, 26 + vs + 6);
  });
  ctx.strokeStyle = C.line; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(0, 84); ctx.lineTo(w, 84); ctx.stroke();
}

function textoConexion(d) {
  const n = d.conexiones === null || d.conexiones === undefined || d.conexiones === '' ? null : Number(d.conexiones);
  const T = tx(d.idioma);
  return n === null ? '' : n === 0 ? T.directo : n === 1 ? T.una : T.varias(n);
}

function cVuelo(ctx, d, w) {
  const T = tx(d.idioma);
  font(ctx, 700, 20, F.body);
  const pw = Math.max(92, Math.round(ctx.measureText(T.vuelo).width + 3 * (T.vuelo.length - 1) + 44));
  ctx.fillStyle = C.navy; rr(ctx, 0, 0, pw, 44, 22); ctx.fill();
  ctx.fillStyle = C.white; spaced(ctx, T.vuelo, pw / 2, 30, 3, 'center');
  font(ctx, 800, 32, F.body); ctx.fillStyle = C.ink;
  ctx.fillText(fmtFechaLarga(d.fechaIda, d.idioma) || '—', pw + 20, 34);
  const compania = [d.aerolinea, d.numeroVuelo].filter(Boolean).join('  ·  ') || T.aerolinea;
  font(ctx, 700, 22, F.body);
  const cwid = ctx.measureText(compania).width + 56;
  ctx.fillStyle = 'rgba(255,107,0,0.10)'; rr(ctx, w - cwid, 0, cwid, 44, 22); ctx.fill();
  ctx.strokeStyle = C.orange; ctx.lineWidth = 2; rr(ctx, w - cwid, 0, cwid, 44, 22); ctx.stroke();
  ctx.fillStyle = '#C24E00'; ctx.textAlign = 'center'; ctx.fillText(compania, w - cwid / 2, 30); ctx.textAlign = 'left';

  const y = 64;
  font(ctx, 400, 140, F.display); ctx.fillStyle = C.navy;
  ctx.fillText(d.origenCodigo || '---', -4, y + 100);
  ctx.textAlign = 'right'; ctx.fillText(d.destinoCodigo || '---', w + 4, y + 100); ctx.textAlign = 'left';
  font(ctx, 600, 22, F.body); ctx.fillStyle = C.muted;
  ctx.fillText((d.origenCiudad || '').toUpperCase(), 0, y + 134);
  ctx.textAlign = 'right'; ctx.fillText((d.destinoCiudad || '').toUpperCase(), w, y + 134); ctx.textAlign = 'left';

  const lx0 = 230; const lx1 = w - 230; const ly = y + 62; const lmid = w / 2;
  ctx.setLineDash([2, 12]); ctx.lineCap = 'round'; ctx.strokeStyle = C.navy; ctx.lineWidth = 4;
  ctx.beginPath(); ctx.moveTo(lx0, ly); ctx.lineTo(lmid - 40, ly); ctx.moveTo(lmid + 40, ly); ctx.lineTo(lx1, ly); ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = C.orange; ctx.beginPath(); ctx.arc(lx0 - 12, ly, 8, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(lx1 + 12, ly, 8, 0, Math.PI * 2); ctx.fill();
  plane(ctx, lmid, ly, 56, Math.PI / 2, C.orange);
  if (d.duracion) {
    font(ctx, 700, 22, F.body);
    const dw = ctx.measureText(d.duracion).width + 66;
    ctx.fillStyle = C.soft; rr(ctx, lmid - dw / 2, ly - 68, dw, 40, 20); ctx.fill();
    icono(ctx, 'clock', lmid - dw / 2 + 16, ly - 58, 20, C.navy);
    ctx.fillStyle = C.navy; ctx.fillText(d.duracion, lmid - dw / 2 + 44, ly - 40);
  }
  const conx = textoConexion(d);
  if (conx) { font(ctx, 600, 22, F.body); ctx.fillStyle = C.muted; ctx.textAlign = 'center'; ctx.fillText(conx, lmid, ly + 48); ctx.textAlign = 'left'; }

  font(ctx, 600, 17, F.body); ctx.fillStyle = C.muted;
  spaced(ctx, T.salida, 0, y + 186, 3); spaced(ctx, T.llegada, w, y + 186, 3, 'right');
  font(ctx, 800, 60, F.body); ctx.fillStyle = C.ink;
  ctx.fillText(d.salida || '--:--', 0, y + 242);
  ctx.textAlign = 'right'; ctx.fillText(d.llegada || '--:--', w, y + 242); ctx.textAlign = 'left';
}

function filasPrecio(d) {
  const T = tx(d.idioma);
  const filas = [['user', `${d.adultos || 1} ${(d.adultos || 1) > 1 ? 'adultos' : 'adulto'}`]];
  if (d.bagagem) filas.push(['bag', d.bagagem]);
  if (d.cabina) filas.push(['seat', `${T.clase} ${T.cabina(d.cabina)}`]);
  return filas;
}

function cPrecio(ctx, d, w) {
  const T = tx(d.idioma);
  ctx.save(); shadow(ctx, 30, 12, 0.25);
  const pg = ctx.createLinearGradient(0, 0, w, H_PRECIO);
  pg.addColorStop(0, C.navyDeep); pg.addColorStop(1, C.navy);
  ctx.fillStyle = pg; rr(ctx, 0, 0, w, H_PRECIO, 32); ctx.fill();
  ctx.restore();
  ctx.fillStyle = C.orange; rr(ctx, 0, 36, 8, H_PRECIO - 72, 4); ctx.fill();

  const px = 52; const vx = Math.round(w * 0.6);
  font(ctx, 600, 18, F.body); ctx.fillStyle = 'rgba(255,255,255,0.7)';
  spaced(ctx, T.valorTotal, px, 62, 3.4);
  const valor = fmtReales(d.precio);
  const vs = fitSize(ctx, valor, 800, F.body, 112, 56, vx - px - 90 - 30);
  font(ctx, 700, Math.round(vs * 0.42), F.body);
  const wRs = ctx.measureText('R$').width + 14;
  ctx.fillStyle = C.orangeSoft; ctx.fillText('R$', px, 62 + vs * 0.92);
  font(ctx, 800, vs, F.body); ctx.fillStyle = C.white; ctx.fillText(valor, px + wRs, 62 + vs * 0.92);
  font(ctx, 500, 22, F.body); ctx.fillStyle = 'rgba(255,255,255,0.78)';
  ctx.fillText(`${T.validoHasta} ${fmtFechaCorta(d.validoHasta) || '—'}`, px, H_PRECIO - 40);

  ctx.strokeStyle = 'rgba(255,255,255,0.16)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(vx, 44); ctx.lineTo(vx, H_PRECIO - 44); ctx.stroke();
  let fy = 62;
  filasPrecio(d).forEach(([ic, texto]) => {
    icono(ctx, ic, vx + 36, fy - 22, 30, C.orangeSoft);
    font(ctx, 600, 23, F.body); ctx.fillStyle = C.white;
    const ls = wrap(ctx, texto, w - (vx + 84) - 16).slice(0, 2);
    ls.forEach((l, i) => ctx.fillText(l, vx + 84, fy + i * 28));
    fy += 66 + (ls.length > 1 ? 12 : 0);
  });
}

function pagosLista(d) { return d.pagos && d.pagos.length ? d.pagos : ['PIX']; }

function cPago(ctx, d, w) {
  const T = tx(d.idioma);
  const pagos = pagosLista(d);
  const soloPix = pagos.length === 1 && pagos[0] === 'PIX';
  ctx.fillStyle = C.soft; rr(ctx, 0, 0, w, H_PAGO, 28); ctx.fill();
  ctx.fillStyle = C.white; ctx.beginPath(); ctx.arc(70, H_PAGO / 2, 38, 0, Math.PI * 2); ctx.fill();
  icono(ctx, soloPix ? 'pix' : 'card', 50, H_PAGO / 2 - 20, 40, C.navy);
  font(ctx, 600, 17, F.body); ctx.fillStyle = C.muted;
  spaced(ctx, pagos.length > 1 ? T.formasPago : T.formaPago, 136, 40, 3);
  const textoPago = soloPix ? T.pagoPix : pagos.map(T.pago).join('  ·  ');
  const ps = fitSize(ctx, textoPago, 800, F.body, 38, 22, w - 136 - 36);
  ctx.fillStyle = C.ink; ctx.fillText(textoPago, 136, 40 + ps + 6);
  if (pagos.includes('Cartão de crédito') && Number(d.parcelas) > 1) {
    font(ctx, 500, 22, F.body); ctx.fillStyle = C.muted;
    ctx.fillText(T.tarjetaHasta(d.parcelas), 136, H_PAGO - 14);
  }
}

export const NOTA_LH = 36; export const NOTA_GAP = 12;
export const alturaNotas = (bloques) => (bloques.length ? 70 + bloques.reduce((a, l) => a + l.length * NOTA_LH + NOTA_GAP, 0) + 4 : 0);

export function cNotas(ctx, bloques, w, hTotal, titulo = 'OBSERVAÇÕES') {
  ctx.fillStyle = C.orange; rr(ctx, 0, 4, 6, hTotal - 8, 3); ctx.fill();
  font(ctx, 700, 19, F.body); ctx.fillStyle = C.navy;
  spaced(ctx, titulo, 32, 32, 3);
  let ty = 78;
  bloques.forEach((ls) => {
    icono(ctx, 'check', 32, ty - 21, 24, C.orange);
    font(ctx, 400, 25, F.body); ctx.fillStyle = C.ink;
    ls.forEach((l, i) => ctx.fillText(l, 32 + 38, ty + i * NOTA_LH));
    ty += ls.length * NOTA_LH + NOTA_GAP;
  });
}

// Recorta las observaciones para que quepan en `hMax` (alto base); añade "…" a la última línea.
export function recortarNotas(bloques, hMax) {
  const out = []; let h = 70 + 4;
  for (const ls of bloques) {
    const cabe = Math.floor((hMax - h - NOTA_GAP) / NOTA_LH);
    if (cabe <= 0) break;
    if (ls.length <= cabe) { out.push(ls); h += ls.length * NOTA_LH + NOTA_GAP; } else {
      const cortado = ls.slice(0, cabe); cortado[cabe - 1] = `${cortado[cabe - 1].replace(/[\s.,;:]+$/, '')}…`;
      out.push(cortado); break;
    }
  }
  return out;
}

// ── Pie ──────────────────────────────────────────────────────────────
export function dibujarPie(ctx, d, W, y, h, { compacto = false } = {}) {
  const T = tx(d.idioma);
  const fg = ctx.createLinearGradient(0, y, W, y + h);
  fg.addColorStop(0, C.navyDeep); fg.addColorStop(1, C.navy);
  ctx.fillStyle = fg; ctx.fillRect(0, y, W, h);
  ctx.fillStyle = C.orange; ctx.fillRect(0, y, W, compacto ? 6 : 8);
  const yc = y + (compacto ? 6 : 8) + (h - (compacto ? 6 : 8)) / 2;
  const mx = compacto ? 40 : 56;
  const r = compacto ? 24 : 30;
  ctx.fillStyle = C.green; ctx.beginPath(); ctx.arc(mx + r, yc, r, 0, Math.PI * 2); ctx.fill();
  icono(ctx, 'phone', mx + r - r / 2, yc - r / 2, r, C.white);
  font(ctx, 800, compacto ? 30 : 34, F.body); ctx.fillStyle = C.white;
  ctx.fillText(d.whatsapp || WHATSAPP_DEFECTO, mx + r * 2 + 18, yc + (compacto ? 2 : 4));
  font(ctx, 500, compacto ? 17 : 20, F.body); ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.fillText(T.wsp, mx + r * 2 + 18, yc + (compacto ? 28 : 34));
  if (!compacto) {
    icono(ctx, 'shield', W * 0.44, yc - 16, 32, C.orangeSoft);
    font(ctx, 600, 22, F.body); ctx.fillStyle = C.white;
    ctx.fillText(T.soporte1, W * 0.44 + 46, yc - 2); ctx.fillText(T.soporte2, W * 0.44 + 46, yc + 26);
  }
  font(ctx, 600, compacto ? 56 : 72, F.script); ctx.fillStyle = C.orangeSoft; ctx.textAlign = 'right';
  ctx.fillText(T.buenViaje, W - mx, yc + (compacto ? 18 : 22)); ctx.textAlign = 'left';
}

// ── A4 y Story: misma composición; se ajusta el tamaño al alto disponible ──
function dibujarLargo(ctx, d, logo, formato) {
  const { w: W, h: H } = formato;
  const OVER = 60; const FOOT = 150; const GAPF = 30; const HERO_MIN = 600; const PAD = 44; const CARD_X = 40;
  const IW = W - 80 - 96;
  const notas = (d.notas || []).filter(Boolean);
  const niveles = [1, 0.94, 0.88, 0.82, 0.78, 0.74];

  let plan = null;
  niveles.forEach((k, i) => {
    if (plan && !plan.recortar) return;
    const cw = IW / k;
    font(ctx, 400, 25, F.body);
    const bloques = notas.map((t) => wrap(ctx, t, cw - 32 - 38 - 24));
    const hNotas = alturaNotas(bloques);
    const seq = H_META + H_VUELO + GAP + H_PRECIO + GAP + H_PAGO + (hNotas ? GAP + hNotas : 0);
    const cardH = PAD + seq * k + 40;
    const heroH = H - FOOT - GAPF + OVER - cardH;
    const ultimo = i === niveles.length - 1;
    if (heroH >= HERO_MIN) plan = { k, cw, bloques, hNotas, cardH, heroH, recortar: false };
    else if (ultimo) plan = { k, cw, bloques, hNotas, cardH, heroH, recortar: true };
    else plan = { recortar: true };
  });
  // si ningún nivel cabía, `plan` es el del último nivel con recortar=true
  let { k, cw, bloques, hNotas, cardH, heroH } = plan;
  let desbordo = false;
  if (plan.recortar) {
    const exceso = HERO_MIN - heroH;               // alto (en px reales) que falta
    const hMax = Math.max(0, hNotas - exceso / k); // alto base permitido para notas
    const recortado = hMax > 70 + 4 + NOTA_LH ? recortarNotas(bloques, hMax) : [];
    desbordo = recortado.reduce((a, l) => a + l.length, 0) < bloques.reduce((a, l) => a + l.length, 0);
    bloques = recortado; hNotas = alturaNotas(bloques);
    const seq = H_META + H_VUELO + GAP + H_PRECIO + GAP + H_PAGO + (hNotas ? GAP + hNotas : 0);
    cardH = PAD + seq * k + 40;
    heroH = H - FOOT - GAPF + OVER - cardH;
  }

  ctx.fillStyle = C.paper; ctx.fillRect(0, 0, W, H);
  cabeceraLarga(ctx, d, logo, W, heroH);

  const cardTop = heroH - OVER;
  ctx.save(); shadow(ctx, 50, 18, 0.28);
  ctx.fillStyle = C.white; rr(ctx, CARD_X, cardTop, W - 80, cardH, 42); ctx.fill();
  ctx.restore();

  const x0 = CARD_X + 48; let y = cardTop + PAD;
  const pieza = (alto, fn, gap = 0) => {
    ctx.save(); ctx.translate(x0, y); ctx.scale(k, k); fn(); ctx.restore();
    y += (alto + gap) * k;
  };
  pieza(H_META, () => cMeta(ctx, d, cw));
  pieza(H_VUELO, () => cVuelo(ctx, d, cw), GAP);
  pieza(H_PRECIO, () => cPrecio(ctx, d, cw), GAP);
  pieza(H_PAGO, () => cPago(ctx, d, cw), hNotas ? GAP : 0);
  if (hNotas) pieza(hNotas, () => cNotas(ctx, bloques, cw, hNotas, tx(d.idioma).observaciones));

  dibujarPie(ctx, d, W, H - FOOT, FOOT);
  return desbordo;
}

// ── Cuadrado 1080×1080: composición propia, más compacta ─────────────
// Cabecera del cuadrado 1080×1080 (ocupa los primeros ~420 px): logo, lema, etiqueta y título.
export function cabeceraCuadrada(ctx, logo, W, { etiqueta, titulo, cliente, tituloMax = 112, idioma }) {
  const T = tx(idioma);
  ctx.save(); ctx.beginPath(); ctx.rect(0, 0, W, 450); ctx.clip();
  dibujarCabecera(ctx, W, 450, { estela: false });
  ctx.restore();
  recuadroLogo(ctx, logo, 40, 40, 400, 210, 30);
  ctx.textAlign = 'right';
  font(ctx, 600, 46, F.script); ctx.fillStyle = C.white; ctx.fillText(T.lema1, W - 40, 86);
  ctx.fillStyle = C.orangeSoft; ctx.fillText(T.lema2, W - 40, 134);
  ctx.textAlign = 'left';

  font(ctx, 600, 21, F.body); ctx.fillStyle = C.orangeSoft;
  spaced(ctx, etiqueta, 40, 296, 5);
  const tam = fitSize(ctx, titulo, 400, F.display, tituloMax, 64, W - 80);
  ctx.fillStyle = C.white; ctx.fillText(titulo, 38, 296 + 6 + tam * 0.8);
  if (cliente) {
    font(ctx, 600, 22, F.body); ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.textAlign = 'right'; ctx.fillText(`${T.preparadoPara.charAt(0)}${T.preparadoPara.slice(1).toLowerCase()} ${cliente}`, W - 40, 196); ctx.textAlign = 'left';
  }
}

function dibujarCuadrado(ctx, d, logo, formato) {
  const { w: W, h: H } = formato;
  const T = tx(d.idioma);
  ctx.fillStyle = C.paper; ctx.fillRect(0, 0, W, H);

  cabeceraCuadrada(ctx, logo, W, {
    etiqueta: `${T.proximoDestino}  ·  ${String(d.destinoPais || 'BRASIL').toUpperCase()}`,
    titulo: String(d.destinoCiudad || d.destinoCodigo || 'DESTINO').toUpperCase(),
    cliente: d.cliente,
    idioma: d.idioma,
  });

  const CT = 418; const FOOT = 84; const CB = H - FOOT - 18; const CH = CB - CT;
  ctx.save(); shadow(ctx, 40, 14, 0.28);
  ctx.fillStyle = C.white; rr(ctx, 28, CT, W - 56, CH, 36); ctx.fill();
  ctx.restore();

  const x0 = 28 + 34; const iw = W - 56 - 68; let y = CT + 28;
  // vuelo compacto
  ctx.save(); ctx.translate(x0, y); ctx.scale(0.78, 0.78);
  const cw = iw / 0.78;
  font(ctx, 700, 20, F.body);
  const pw = Math.max(92, Math.round(ctx.measureText(T.vuelo).width + 3 * (T.vuelo.length - 1) + 44));
  ctx.fillStyle = C.navy; rr(ctx, 0, 0, pw, 44, 22); ctx.fill();
  ctx.fillStyle = C.white; spaced(ctx, T.vuelo, pw / 2, 30, 3, 'center');
  font(ctx, 800, 32, F.body); ctx.fillStyle = C.ink; ctx.fillText(fmtFechaLarga(d.fechaIda, d.idioma) || '—', pw + 20, 34);
  const comp = [d.aerolinea, d.numeroVuelo].filter(Boolean).join('  ·  ') || T.aerolinea;
  font(ctx, 700, 22, F.body);
  const cwid = ctx.measureText(comp).width + 56;
  ctx.fillStyle = 'rgba(255,107,0,0.10)'; rr(ctx, cw - cwid, 0, cwid, 44, 22); ctx.fill();
  ctx.strokeStyle = C.orange; ctx.lineWidth = 2; rr(ctx, cw - cwid, 0, cwid, 44, 22); ctx.stroke();
  ctx.fillStyle = '#C24E00'; ctx.textAlign = 'center'; ctx.fillText(comp, cw - cwid / 2, 30); ctx.textAlign = 'left';
  ctx.restore();
  y += 44 * 0.78 + 10;

  ctx.save(); ctx.translate(x0, y); ctx.scale(0.78, 0.78);
  const base = 100;
  font(ctx, 400, 120, F.display); ctx.fillStyle = C.navy;
  ctx.fillText(d.origenCodigo || '---', -3, base);
  ctx.textAlign = 'right'; ctx.fillText(d.destinoCodigo || '---', cw + 3, base); ctx.textAlign = 'left';
  font(ctx, 600, 21, F.body); ctx.fillStyle = C.muted;
  ctx.fillText((d.origenCiudad || '').toUpperCase(), 0, base + 32);
  ctx.textAlign = 'right'; ctx.fillText((d.destinoCiudad || '').toUpperCase(), cw, base + 32); ctx.textAlign = 'left';
  const ly = base - 40; const lm = cw / 2;
  ctx.setLineDash([2, 12]); ctx.lineCap = 'round'; ctx.strokeStyle = C.navy; ctx.lineWidth = 4;
  ctx.beginPath(); ctx.moveTo(200, ly); ctx.lineTo(lm - 40, ly); ctx.moveTo(lm + 40, ly); ctx.lineTo(cw - 200, ly); ctx.stroke(); ctx.setLineDash([]);
  ctx.fillStyle = C.orange; ctx.beginPath(); ctx.arc(188, ly, 8, 0, Math.PI * 2); ctx.fill(); ctx.beginPath(); ctx.arc(cw - 188, ly, 8, 0, Math.PI * 2); ctx.fill();
  plane(ctx, lm, ly, 52, Math.PI / 2, C.orange);
  if (d.duracion) {
    font(ctx, 700, 21, F.body); const dw = ctx.measureText(d.duracion).width + 62;
    ctx.fillStyle = C.soft; rr(ctx, lm - dw / 2, ly - 64, dw, 38, 19); ctx.fill();
    icono(ctx, 'clock', lm - dw / 2 + 15, ly - 55, 20, C.navy);
    ctx.fillStyle = C.navy; ctx.fillText(d.duracion, lm - dw / 2 + 42, ly - 38);
  }
  const conx = textoConexion(d);
  if (conx) { font(ctx, 600, 21, F.body); ctx.fillStyle = C.muted; ctx.textAlign = 'center'; ctx.fillText(conx, lm, ly + 44); ctx.textAlign = 'left'; }
  font(ctx, 800, 54, F.body); ctx.fillStyle = C.ink;
  ctx.fillText(d.salida || '--:--', 0, base + 92);
  ctx.textAlign = 'right'; ctx.fillText(d.llegada || '--:--', cw, base + 92); ctx.textAlign = 'left';
  ctx.restore();
  y += (base + 92 + 14) * 0.78;

  // precio compacto (alto 160)
  const PH = 150;
  ctx.save(); ctx.translate(x0, y);
  const pg = ctx.createLinearGradient(0, 0, iw, PH); pg.addColorStop(0, C.navyDeep); pg.addColorStop(1, C.navy);
  ctx.fillStyle = pg; rr(ctx, 0, 0, iw, PH, 26); ctx.fill();
  ctx.fillStyle = C.orange; rr(ctx, 0, 26, 7, PH - 52, 3.5); ctx.fill();
  const vx = Math.round(iw * 0.58);
  font(ctx, 600, 16, F.body); ctx.fillStyle = 'rgba(255,255,255,0.7)'; spaced(ctx, T.valorTotal, 34, 36, 3);
  const valor = fmtReales(d.precio);
  const vs = fitSize(ctx, valor, 800, F.body, 76, 44, vx - 34 - 70 - 20);
  font(ctx, 700, Math.round(vs * 0.42), F.body); const wRs = ctx.measureText('R$').width + 10;
  ctx.fillStyle = C.orangeSoft; ctx.fillText('R$', 34, 36 + vs * 0.9);
  font(ctx, 800, vs, F.body); ctx.fillStyle = C.white; ctx.fillText(valor, 34 + wRs, 36 + vs * 0.9);
  font(ctx, 500, 19, F.body); ctx.fillStyle = 'rgba(255,255,255,0.78)'; ctx.fillText(`${T.validoHasta} ${fmtFechaCorta(d.validoHasta) || '—'}`, 34, PH - 18);
  ctx.strokeStyle = 'rgba(255,255,255,0.16)'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(vx, 26); ctx.lineTo(vx, PH - 26); ctx.stroke();
  let fy = 46;
  filasPrecio(d).forEach(([ic, texto]) => {
    icono(ctx, ic, vx + 26, fy - 19, 24, C.orangeSoft);
    font(ctx, 600, 20, F.body); ctx.fillStyle = C.white;
    const l = lineaAjustada(ctx, texto, 600, 20, 16, iw - (vx + 62) - 14);
    font(ctx, 600, l.s, F.body); ctx.fillText(l.t, vx + 62, fy);
    fy += 38;
  });
  ctx.restore();
  y += PH + 10;

  // pago en una línea (alto 64)
  const PY = 64; const pagos = pagosLista(d); const soloPix = pagos.length === 1 && pagos[0] === 'PIX';
  ctx.fillStyle = C.soft; rr(ctx, x0, y, iw, PY, 22); ctx.fill();
  icono(ctx, soloPix ? 'pix' : 'card', x0 + 18, y + 14, 36, C.navy);
  let textoPago = soloPix ? T.pagoPix : pagos.map(T.pago).join('  ·  ');
  if (pagos.includes('Cartão de crédito') && Number(d.parcelas) > 1) textoPago += `  ${T.hastaCuotas(d.parcelas)}`;
  const ps = fitSize(ctx, textoPago, 800, F.body, 28, 17, iw - 90 - 20);
  ctx.fillStyle = C.ink; ctx.fillText(textoPago, x0 + 70, y + PY / 2 + ps * 0.35);
  y += PY + 10;

  // observaciones: lo que quepa (máx. 2 líneas) en el espacio que queda
  const notas = (d.notas || []).filter(Boolean);
  let desbordo = false;
  if (notas.length) {
    font(ctx, 400, 21, F.body);
    const lineas = []; notas.forEach((t) => wrap(ctx, t, iw - 36).forEach((l, i) => lineas.push({ t: l, bullet: i === 0 })));
    const libre = CB - 14 - y; const max = Math.max(0, Math.floor(libre / 27));
    const vis = lineas.slice(0, max);
    if (lineas.length > max) {
      desbordo = true;
      if (vis.length) vis[vis.length - 1] = { ...vis[vis.length - 1], t: `${vis[vis.length - 1].t.replace(/[\s.,;:]+$/, '')}…` };
    }
    vis.forEach((l, i) => {
      if (l.bullet) icono(ctx, 'check', x0, y + i * 27 + 2, 18, C.orange);
      ctx.fillStyle = C.ink; ctx.fillText(l.t, x0 + 28, y + i * 27 + 17);
    });
  }

  dibujarPie(ctx, d, W, H - FOOT, FOOT, { compacto: true });
  return desbordo;
}


// ── Orçamento con varias líneas: vuelo + hotel / servicios / ómnibus ──
// d.items = [{ tipo: 'hotel'|'servicio'|'omnibus', ... }], d.resumen = [{ etiqueta, precio }] (una por línea,
// el vuelo primero), d.precio = total, d.mostrarPrecioLinea. Sin vuelo: d.sinVuelo.
export const esMulti = (d) => !!d.sinVuelo || (Array.isArray(d.items) && d.items.length > 0);
const cap = (t) => `${t.charAt(0)}${t.slice(1).toLowerCase()}`;
const fmtDM = (iso) => { const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}` : ''; };
const iconoItem = (tipo) => (tipo === 'hotel' ? 'bed' : tipo === 'omnibus' ? 'bus' : 'check');

function lineasItem(d, it) {
  const T = tx(d.idioma);
  if (it.tipo === 'hotel') {
    const n = Number(it.noches) || 0;
    return {
      etiqueta: `${T.hotel}${it.ciudad ? `  ·  ${String(it.ciudad).toUpperCase()}` : ''}`, titulo: it.nombre || T.hotel,
      detalles: [
        [it.entrada && it.salida ? `${fmtDM(it.entrada)} → ${fmtDM(it.salida)}` : '', n ? `${n} ${n === 1 ? T.noche : T.noches}` : ''].filter(Boolean).join('  ·  '),
        [it.huespedes ? T.huespedes(Number(it.huespedes)) : '', it.habitaciones ? T.habitaciones(Number(it.habitaciones)) : '', it.regimen || ''].filter(Boolean).join('  ·  '),
      ].filter(Boolean),
    };
  }
  if (it.tipo === 'omnibus') {
    return {
      etiqueta: T.bus, titulo: [it.origen, it.destino].filter(Boolean).join(' → ') || T.bus,
      detalles: [
        [fmtDM(it.fecha), it.sale ? `${cap(T.salida)} ${it.sale}` : '', it.llega ? `${cap(T.llegada)} ${it.llega}` : ''].filter(Boolean).join('  ·  '),
        it.empresa || '',
      ].filter(Boolean),
    };
  }
  return { etiqueta: T.servicio, titulo: it.titulo || T.servicio, detalles: [it.detalle || ''].filter(Boolean) };
}
const alturaItem = (d, it) => 74 + lineasItem(d, it).detalles.length * 32 + 12;

function cItem(ctx, d, it, w) {
  const H = alturaItem(d, it);
  const L = lineasItem(d, it);
  ctx.fillStyle = C.soft; rr(ctx, 0, 0, w, H, 26); ctx.fill();
  ctx.fillStyle = C.navy; ctx.beginPath(); ctx.arc(52, H / 2, 34, 0, Math.PI * 2); ctx.fill();
  icono(ctx, iconoItem(it.tipo), 52 - 17, H / 2 - 17, 34, C.white);
  const x = 110; const ancho = w - x - 28;
  font(ctx, 700, 15, F.body); ctx.fillStyle = C.muted;
  const e = lineaAjustada(ctx, L.etiqueta, 700, 15, 11, ancho); font(ctx, 700, e.s, F.body); spaced(ctx, e.t, x, 34, 2.4);
  const t = lineaAjustada(ctx, L.titulo, 800, 32, 20, ancho);
  font(ctx, 800, t.s, F.body); ctx.fillStyle = C.ink; ctx.fillText(t.t, x, 34 + 8 + t.s);
  L.detalles.forEach((txt, i) => {
    const dt = lineaAjustada(ctx, txt, 500, 22, 15, ancho);
    font(ctx, 500, dt.s, F.body); ctx.fillStyle = C.muted; ctx.fillText(dt.t, x, 34 + 8 + t.s + 34 + i * 32);
  });
}

// Fila con lo que incluye el vuelo (pasajero, equipaje, clase) cuando el precio va en el total.
function cInfoVuelo(ctx, d, w) {
  const items = filasPrecio(d);
  const cw = w / items.length;
  items.forEach(([ic, texto], i) => {
    const x = i * cw;
    icono(ctx, ic, x, 8, 28, C.navy);
    const l = lineaAjustada(ctx, texto, 600, 22, 14, cw - 44);
    font(ctx, 600, l.s, F.body); ctx.fillStyle = C.ink; ctx.fillText(l.t, x + 38, 30);
  });
}
const alturaInfoVuelo = (d) => (filasPrecio(d).length > 1 ? 48 : 0);

const H_FILA_TOTAL = 46;
const alturaTotal = (d) => 56 + d.resumen.length * H_FILA_TOTAL + 20 + 190;

function cTotal(ctx, d, w) {
  const T = tx(d.idioma);
  const n = d.resumen.length; const H = alturaTotal(d);
  ctx.save(); shadow(ctx, 30, 12, 0.25);
  const pg = ctx.createLinearGradient(0, 0, w, H);
  pg.addColorStop(0, C.navyDeep); pg.addColorStop(1, C.navy);
  ctx.fillStyle = pg; rr(ctx, 0, 0, w, H, 32); ctx.fill();
  ctx.restore();
  ctx.fillStyle = C.orange; rr(ctx, 0, 36, 8, H - 72, 4); ctx.fill();

  const px = 52;
  font(ctx, 600, 16, F.body); ctx.fillStyle = 'rgba(255,255,255,0.7)';
  spaced(ctx, d.mostrarPrecioLinea ? T.resumen : T.incluyeLista, px, 46, 3.2);
  d.resumen.forEach((l, i) => {
    const y = 56 + i * H_FILA_TOTAL + 32;
    if (d.mostrarPrecioLinea) {
      const precio = `R$ ${fmtReales(l.precio)}`;
      font(ctx, 700, 26, F.body); const wp = ctx.measureText(precio).width;
      const li = lineaAjustada(ctx, l.etiqueta, 600, 24, 15, w - px * 2 - wp - 24);
      font(ctx, 600, li.s, F.body); ctx.fillStyle = C.white; ctx.fillText(li.t, px, y);
      font(ctx, 700, 26, F.body); ctx.fillStyle = C.orangeSoft; ctx.textAlign = 'right'; ctx.fillText(precio, w - px, y); ctx.textAlign = 'left';
    } else {
      icono(ctx, 'check', px, y - 20, 24, C.orangeSoft);
      const li = lineaAjustada(ctx, l.etiqueta, 600, 24, 15, w - px * 2 - 40);
      font(ctx, 600, li.s, F.body); ctx.fillStyle = C.white; ctx.fillText(li.t, px + 40, y);
    }
  });
  const ys = 56 + n * H_FILA_TOTAL + 8;
  ctx.strokeStyle = 'rgba(255,255,255,0.22)'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(px, ys); ctx.lineTo(w - px, ys); ctx.stroke();
  font(ctx, 600, 18, F.body); ctx.fillStyle = 'rgba(255,255,255,0.75)';
  spaced(ctx, T.valorTotal, px, ys + 40, 3.4);
  const valor = fmtReales(d.precio);
  const vs = fitSize(ctx, valor, 800, F.body, 92, 52, w - px * 2 - 90);
  font(ctx, 700, Math.round(vs * 0.42), F.body); const wRs = ctx.measureText('R$').width + 14;
  ctx.fillStyle = C.orangeSoft; ctx.fillText('R$', px, ys + 40 + vs * 0.95);
  font(ctx, 800, vs, F.body); ctx.fillStyle = C.white; ctx.fillText(valor, px + wRs, ys + 40 + vs * 0.95);
  font(ctx, 500, 22, F.body); ctx.fillStyle = 'rgba(255,255,255,0.78)';
  ctx.fillText(`${T.validoHasta} ${fmtFechaCorta(d.validoHasta) || '—'}`, px, H - 30);
}

function dibujarLargoMulti(ctx, d, logo, formato) {
  const { w: W, h: H } = formato;
  const OVER = 60; const FOOT = 150; const GAPF = 30; const HERO_MAX = 860; const PAD = 44; const CARD_X = 40;
  const IW = W - 80 - 96;
  const items = d.items || [];
  const conVuelo = !d.sinVuelo;
  const hInfo = conVuelo ? alturaInfoVuelo(d) : 0;
  const notasTodas = (d.notas || []).filter(Boolean);
  const niveles = [1.2, 1.1, 1, 0.94, 0.88, 0.82, 0.78, 0.74, 0.68, 0.62, 0.58];

  const medir = (k, heroMin, conNotas) => {
    const cw = IW / k;
    font(ctx, 400, 25, F.body);
    const bloques = conNotas ? notasTodas.map((t) => wrap(ctx, t, cw - 32 - 38 - 24)) : [];
    const hNotas = alturaNotas(bloques);
    const piezas = [H_META];
    if (conVuelo) { piezas.push(H_VUELO); if (hInfo) piezas.push(hInfo); }
    items.forEach((it) => piezas.push(alturaItem(d, it)));
    piezas.push(alturaTotal(d), H_PAGO);
    if (hNotas) piezas.push(hNotas);
    const contenido = piezas.reduce((a, h) => a + h, 0) + (piezas.length - 1) * GAP;
    return { k, cw, bloques, hNotas, contenido, sobra: (H - FOOT - GAPF + OVER - heroMin - 2 * PAD) / k - contenido };
  };
  let plan = null;
  [560, 500, 430].forEach((heroMin) => niveles.forEach((k) => { if (!plan) { const m = medir(k, heroMin, true); if (m.sobra >= 0) plan = m; } }));
  let desbordo = false;
  if (!plan) { // no caben ni las observaciones: se quitan y se avisa
    desbordo = notasTodas.length > 0;
    [430].forEach((heroMin) => niveles.forEach((k) => { if (!plan) { const m = medir(k, heroMin, false); if (m.sobra >= 0) plan = m; } }));
    if (!plan) { plan = medir(niveles[niveles.length - 1], 430, false); desbordo = true; }
  }
  const { k, cw, bloques, hNotas } = plan;
  let cardH = 2 * PAD + plan.contenido * k;
  let heroH = H - FOOT - GAPF + OVER - cardH;
  let offset = 0;
  if (heroH > HERO_MAX) { cardH += heroH - HERO_MAX; offset = (heroH - HERO_MAX) / 2; heroH = HERO_MAX; }

  ctx.fillStyle = C.paper; ctx.fillRect(0, 0, W, H);
  cabeceraLarga(ctx, d, logo, W, heroH);
  const cardTop = heroH - OVER;
  ctx.save(); shadow(ctx, 50, 18, 0.28);
  ctx.fillStyle = C.white; rr(ctx, CARD_X, cardTop, W - 80, cardH, 42); ctx.fill();
  ctx.restore();

  const x0 = CARD_X + 48; let y = cardTop + PAD + offset;
  const pieza = (alto, fn) => { ctx.save(); ctx.translate(x0, y); ctx.scale(k, k); fn(); ctx.restore(); y += (alto + GAP) * k; };
  pieza(H_META, () => cMeta(ctx, d, cw));
  if (conVuelo) { pieza(H_VUELO + (hInfo ? 0 : 0), () => cVuelo(ctx, d, cw)); if (hInfo) { y -= GAP * k; pieza(hInfo, () => cInfoVuelo(ctx, d, cw)); } }
  items.forEach((it) => pieza(alturaItem(d, it), () => cItem(ctx, d, it, cw)));
  pieza(alturaTotal(d), () => cTotal(ctx, d, cw));
  pieza(H_PAGO, () => cPago(ctx, d, cw));
  if (hNotas) pieza(hNotas, () => cNotas(ctx, bloques, cw, hNotas, tx(d.idioma).observaciones));
  dibujarPie(ctx, d, W, H - FOOT, FOOT);
  return desbordo;
}

function dibujarCuadradoMulti(ctx, d, logo, formato) {
  const { w: W, h: H } = formato;
  const T = tx(d.idioma);
  ctx.fillStyle = C.paper; ctx.fillRect(0, 0, W, H);
  cabeceraCuadrada(ctx, logo, W, {
    etiqueta: `${T.proximoDestino}  ·  ${String(d.destinoPais || 'BRASIL').toUpperCase()}`,
    titulo: String(d.destinoCiudad || d.destinoCodigo || 'DESTINO').toUpperCase(),
    cliente: d.cliente, idioma: d.idioma,
  });
  const CT = 418; const FOOT = 84; const CB = H - FOOT - 18;
  ctx.save(); shadow(ctx, 40, 14, 0.28);
  ctx.fillStyle = C.white; rr(ctx, 28, CT, W - 56, CB - CT, 36); ctx.fill();
  ctx.restore();
  const x0 = 28 + 30; const iw = W - 56 - 60; let y = CT + 22;

  // filas: una por línea del resumen (vuelo, hotel, servicios, ómnibus)
  const filas = [];
  if (!d.sinVuelo) {
    filas.push({
      icono: 'plane', titulo: `${cap(T.vuelo)} ${d.origenCodigo || ''} → ${d.destinoCodigo || ''}`.trim(),
      sub: [fmtFechaLarga(d.fechaIda, d.idioma), d.salida && d.llegada ? `${d.salida} → ${d.llegada}` : '', textoConexion(d)].filter(Boolean).join('  ·  '),
    });
  }
  (d.items || []).forEach((it) => { const L = lineasItem(d, it); filas.push({ icono: iconoItem(it.tipo), titulo: L.titulo, sub: L.detalles[0] || '' }); });
  filas.forEach((f, i) => { f.precio = d.resumen[i]?.precio; });

  const FILA = 78; const TOT = 96; const PY = 64;
  const libre = CB - 16 - y - TOT - 10 - PY - 10;
  const k = Math.max(0.5, Math.min(1.12, libre / (filas.length * FILA)));
  const nMax = Math.max(1, Math.floor(libre / (FILA * k)));
  const vis = filas.slice(0, nMax);
  const desbordo = filas.length > vis.length;
  const cw = iw / k;
  ctx.save(); ctx.translate(x0, y); ctx.scale(k, k);
  vis.forEach((f, i) => {
    const yy = i * FILA;
    ctx.fillStyle = C.soft; rr(ctx, 0, yy, cw, FILA - 8, 20); ctx.fill();
    ctx.fillStyle = C.navy; ctx.beginPath(); ctx.arc(40, yy + (FILA - 8) / 2, 26, 0, Math.PI * 2); ctx.fill();
    if (f.icono === 'plane') plane(ctx, 40, yy + (FILA - 8) / 2, 26, Math.PI / 2, C.white); else icono(ctx, f.icono, 40 - 13, yy + (FILA - 8) / 2 - 13, 26, C.white);
    const precio = d.mostrarPrecioLinea && f.precio !== undefined ? `R$ ${fmtReales(f.precio)}` : '';
    font(ctx, 700, 26, F.body); const wp = precio ? ctx.measureText(precio).width + 20 : 0;
    const ancho = cw - 84 - wp - 20;
    const t = lineaAjustada(ctx, f.titulo, 800, 27, 16, ancho); font(ctx, 800, t.s, F.body); ctx.fillStyle = C.ink; ctx.fillText(t.t, 84, yy + 32);
    if (f.sub) { const sb = lineaAjustada(ctx, f.sub, 500, 20, 13, ancho); font(ctx, 500, sb.s, F.body); ctx.fillStyle = C.muted; ctx.fillText(sb.t, 84, yy + 56); }
    if (precio) { font(ctx, 700, 26, F.body); ctx.fillStyle = C.navy; ctx.textAlign = 'right'; ctx.fillText(precio, cw - 22, yy + (FILA - 8) / 2 + 9); ctx.textAlign = 'left'; }
  });
  ctx.restore();
  y += vis.length * FILA * k + 10;

  // total
  ctx.save(); ctx.translate(x0, y);
  const pg = ctx.createLinearGradient(0, 0, iw, TOT); pg.addColorStop(0, C.navyDeep); pg.addColorStop(1, C.navy);
  ctx.fillStyle = pg; rr(ctx, 0, 0, iw, TOT, 24); ctx.fill();
  ctx.fillStyle = C.orange; rr(ctx, 0, 22, 7, TOT - 44, 3.5); ctx.fill();
  font(ctx, 600, 15, F.body); ctx.fillStyle = 'rgba(255,255,255,0.75)'; spaced(ctx, T.valorTotal, 30, 30, 3);
  const valor = fmtReales(d.precio);
  const vs = fitSize(ctx, valor, 800, F.body, 56, 34, iw * 0.55);
  font(ctx, 700, Math.round(vs * 0.45), F.body); const wRs = ctx.measureText('R$').width + 10;
  ctx.fillStyle = C.orangeSoft; ctx.fillText('R$', 30, 30 + vs * 0.95);
  font(ctx, 800, vs, F.body); ctx.fillStyle = C.white; ctx.fillText(valor, 30 + wRs, 30 + vs * 0.95);
  font(ctx, 500, 19, F.body); ctx.fillStyle = 'rgba(255,255,255,0.8)'; ctx.textAlign = 'right';
  ctx.fillText(`${T.validoHasta} ${fmtFechaCorta(d.validoHasta) || '—'}`, iw - 26, TOT / 2 + 6); ctx.textAlign = 'left';
  ctx.restore();
  y += TOT + 10;

  // pago en una línea
  const pagos = pagosLista(d); const soloPix = pagos.length === 1 && pagos[0] === 'PIX';
  ctx.fillStyle = C.soft; rr(ctx, x0, y, iw, PY, 22); ctx.fill();
  icono(ctx, soloPix ? 'pix' : 'card', x0 + 18, y + 14, 36, C.navy);
  let textoPago = soloPix ? T.pagoPix : pagos.map(T.pago).join('  ·  ');
  if (pagos.includes('Cartão de crédito') && Number(d.parcelas) > 1) textoPago += `  ${T.hastaCuotas(d.parcelas)}`;
  const ps = fitSize(ctx, textoPago, 800, F.body, 28, 17, iw - 90 - 20);
  ctx.fillStyle = C.ink; ctx.fillText(textoPago, x0 + 70, y + PY / 2 + ps * 0.35);
  dibujarPie(ctx, d, W, H - FOOT, FOOT, { compacto: true });
  return desbordo;
}

// ── Función principal ────────────────────────────────────────────────
export async function renderOrcamento(d, { formato = 'a4', scale, logoUrl = '/logo-principal.png' } = {}) {
  await asegurarFuentes();
  let logo = null;
  try { logo = await cargarImagen(logoUrl); } catch { /* sin logo: se omite el recuadro */ }

  const fm = FORMATOS[formato] || FORMATOS.a4;
  const esc = scale || fm.escala;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(fm.w * esc);
  canvas.height = Math.round(fm.h * esc);
  const ctx = canvas.getContext('2d');
  ctx.scale(esc, esc);
  ctx.textBaseline = 'alphabetic';

  const multi = esMulti(d);
  if (fm.id === 'cuadrado') canvas.desbordo = multi ? dibujarCuadradoMulti(ctx, d, logo, fm) : dibujarCuadrado(ctx, d, logo, fm);
  else canvas.desbordo = multi ? dibujarLargoMulti(ctx, d, logo, fm) : dibujarLargo(ctx, d, logo, fm);
  return canvas;
}

// ── Salidas ──────────────────────────────────────────────────────────
export const canvasToBlob = (canvas, quality = 0.92, tipo = 'image/jpeg') =>
  new Promise((resolve) => canvas.toBlob((b) => resolve(b), tipo, quality));

// PDF mínimo de una página con el JPEG incrustado (sin librerías). Ancho de página A4.
export async function canvasToPdfBlob(canvas, quality = 0.92) {
  const jpeg = new Uint8Array(await (await canvasToBlob(canvas, quality)).arrayBuffer());
  const pw = 595.28;
  const ph = (pw * canvas.height) / canvas.width;
  const enc = new TextEncoder();
  const chunks = []; const offsets = []; let pos = 0;
  const add = (x) => { const b = typeof x === 'string' ? enc.encode(x) : x; chunks.push(b); pos += b.length; };
  const obj = (n, body) => { offsets[n] = pos; add(`${n} 0 obj\n`); add(body); add('\nendobj\n'); };

  add(new Uint8Array([0x25, 0x50, 0x44, 0x46, 0x2d, 0x31, 0x2e, 0x34, 0x0a, 0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));
  obj(1, '<< /Type /Catalog /Pages 2 0 R >>');
  obj(2, '<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
  obj(3, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pw.toFixed(2)} ${ph.toFixed(2)}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`);
  offsets[4] = pos;
  add(`4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${canvas.width} /Height ${canvas.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`);
  add(jpeg);
  add('\nendstream\nendobj\n');
  const content = `q ${pw.toFixed(2)} 0 0 ${ph.toFixed(2)} 0 0 cm /Im0 Do Q`;
  obj(5, `<< /Length ${content.length} >>\nstream\n${content}\nendstream`);

  const xref = pos;
  let tabla = 'xref\n0 6\n0000000000 65535 f \n';
  for (let i = 1; i <= 5; i += 1) tabla += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
  add(tabla);
  add(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`);
  return new Blob(chunks, { type: 'application/pdf' });
}
